import { useQuery } from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';
import { getLocalStorage } from '@/Utils/CommonUtils';

export interface OrderDetailsResponse {
  order_id: string;
  amount_paid: number;
  currency: string;
  program_name: string;
  plan_type: 'personal' | 'group' | 'webinar';
  duration: number;
  sessions: number;
  selected_coach?: 'general' | 'international_master' | 'grand_master' | null;
  first_class_date?: string | null;
  first_class_time?: string | null;
  coach_name?: string | null;
  has_change_request?: boolean;
  requested_days?: string | null;
  requested_time?: string | null;
  renewal_meta?: Record<string, any> | null;
  is_newcsoc?: boolean;
}

export default function useGetOrderDetails(
  orderId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<OrderDetailsResponse>(
    ['order-details', orderId],
    async () => {
      if (!orderId) {
        throw new Error('Order ID is required');
      }

      const token = await getLocalStorage('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      try {
        const response = await ChessRestClient.get<OrderDetailsResponse>(
          `subscription/v1/get-order-details/?order_id=${encodeURIComponent(
            orderId,
          )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
          },
        );
        return response.data;
      } catch (error: any) {
        console.log('Error fetching order details:', error);
        // If it's a 404 error with data in response, treat it as valid data (payment failed scenario)
        if (error?.response?.status === 404 && error?.response?.data?.data) {
          console.log(
            '404 received with order data, treating as payment failure:',
            error.response.data.data,
          );
          // Return the embedded data from the 404 response
          return error.response;
        }
        // For all other errors, rethrow
        throw error;
      }
    },
    {
      enabled: options?.enabled !== false && !!orderId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 404 errors with data (payment failed scenario)
        if (error?.response?.status === 404 && error?.response?.data?.data) {
          return false;
        }
        // Default retry behavior for other errors
        return failureCount < 3;
      },
    },
  );
}
